import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass, X } from "@phosphor-icons/react";

/**
 * MeslekAutocomplete — Türk ISCO-08 meslek kodu ve adı için typeahead alanı.
 *
 * Kullanıcı serbestçe yazabilir (dışarıdan veri) ve altta öneri listesi çıkar.
 * Öneri seçildiğinde alan "KOD - MESLEK ADI" olarak doldurulur; kullanıcı
 * kendi metnini de bırakabilir (öneri listesi zorunlu değil).
 */
export default function MeslekAutocomplete({
  value,
  onChange,
  placeholder = "Meslek adı veya kod yazın… örn: Duvarcı",
  testId = "emp-meslek",
  disabled = false,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);

  const doSearch = async (q) => {
    if (abortRef.current) abortRef.current.abort();
    const ctl = new AbortController();
    abortRef.current = ctl;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/meslek-codes`, {
        params: { q, limit: 15 },
        signal: ctl.signal,
      });
      setSuggestions(data.results || []);
    } catch (e) {
      if (!axios.isCancel?.(e) && e.name !== "CanceledError") {
        setSuggestions([]);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!open) return;
    debounceRef.current = setTimeout(() => doSearch(value || ""), 180);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line
  }, [value, open]);

  // Dış tıklama ile kapat
  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (s) => {
    onChange(`${s.code} - ${s.name}`);
    setOpen(false);
    setHighlight(-1);
  };

  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, -1));
    } else if (e.key === "Enter" && highlight >= 0 && suggestions[highlight]) {
      e.preventDefault();
      pick(suggestions[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <MagnifyingGlass
          size={13}
          className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
        />
        <Input
          value={value || ""}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder={placeholder}
          disabled={disabled}
          data-testid={testId}
          className="rounded-none sm:rounded-sm pl-7 pr-8"
        />
        {value && !disabled && (
          <button
            type="button"
            onClick={() => { onChange(""); setOpen(true); }}
            data-testid={`${testId}-clear`}
            className="absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 inline-flex items-center justify-center hover:bg-slate-100"
            title="Temizle"
          >
            <X size={11} className="text-slate-500" />
          </button>
        )}
      </div>

      {open && (
        <div
          data-testid={`${testId}-suggestions`}
          className="absolute z-40 left-0 right-0 top-full mt-1 max-h-72 overflow-auto border border-slate-300 bg-white shadow-lg"
        >
          <div className="px-2 py-1 text-[10px] uppercase tracking-widest font-bold text-slate-500 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <span>ISCO-08 Meslek Önerileri</span>
            {loading && <span className="text-slate-400">Yükleniyor…</span>}
          </div>
          {suggestions.length === 0 && !loading && (
            <div className="px-3 py-4 text-xs text-slate-500 text-center">
              Eşleşme yok — istediğiniz metni yazıp kaydedebilirsiniz.
            </div>
          )}
          {suggestions.map((s, i) => (
            <button
              key={s.code}
              type="button"
              onMouseEnter={() => setHighlight(i)}
              onClick={() => pick(s)}
              data-testid={`${testId}-opt-${i}`}
              className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-3 border-b border-slate-100 last:border-0 ${
                i === highlight ? "bg-slate-100" : "hover:bg-slate-50"
              }`}
            >
              <span className="font-mono text-[11px] font-bold text-[#1B4776] whitespace-nowrap">
                {s.code}
              </span>
              <span className="text-slate-800 flex-1 truncate">{s.name}</span>
            </button>
          ))}
          <div className="px-3 py-1.5 text-[9px] text-slate-400 text-right border-t border-slate-200 bg-slate-50">
            Kaynak: TÜRMOB — ISCO-08 Meslek Adı ve Kodları
          </div>
        </div>
      )}
    </div>
  );
}
